import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";
import { api, formatApiError } from "../../api";
import { LogoMark } from "../../components/Logo";

export default function OwnerSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(formatApiError(e, "This invite link is invalid.")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/register-owner", { token, name: form.name, password: form.password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err, "Could not create your account."));
      setBusy(false);
    }
  };

  const field =
    "w-full rounded-2xl border border-sage/20 bg-white/80 px-5 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-sage";

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6" data-testid="owner-setup-page">
      <div className="w-full max-w-md rounded-[1.8rem] border border-sage/12 bg-white/80 p-10 shadow-[0_20px_50px_-20px_rgba(59,87,75,0.2)] backdrop-blur">
        <div className="flex flex-col items-center text-center">
          <LogoMark size={44} />
          <h1 className="mt-4 font-serif text-3xl text-ink">Welcome, owner</h1>
          <p className="mt-2 text-sm leading-relaxed text-ink/50">
            Set your password to take over Bloom Paper Studio. Once finished, only your account can
            manage the shop.
          </p>
        </div>

        {!invite && !error && (
          <div className="mt-10 flex justify-center" data-testid="invite-loading">
            <Loader2 className="animate-spin text-sage" />
          </div>
        )}

        {error && (
          <div className="mt-8 text-center" data-testid="invite-invalid">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-lavender-tint text-lavender-deep">
              <XCircle size={22} />
            </span>
            <p className="mt-4 text-sm leading-relaxed text-ink/65">{error}</p>
            <Link to="/admin/login" className="mt-6 inline-block text-sm text-sage underline underline-offset-4">
              Go to sign in
            </Link>
          </div>
        )}

        {invite && !error && (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">
                Account email (bound to this invite)
              </label>
              <input value={invite.email} readOnly data-testid="owner-email-readonly"
                className={`${field} cursor-not-allowed bg-cream-dark/60 text-ink/60`} />
            </div>
            <input data-testid="owner-name-input" required value={form.name} placeholder="Your name"
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
            <input data-testid="owner-password-input" required type="password" minLength={10}
              value={form.password} placeholder="Password (10+ characters)"
              onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} />
            <input data-testid="owner-password-confirm" required type="password" minLength={10}
              value={form.confirm} placeholder="Confirm password"
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={field} />
            <button data-testid="owner-setup-submit" disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-sage py-4 text-sm font-medium text-cream transition-colors hover:bg-sage-deep disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={15} />}
              Create my owner account
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
